import React, {
  useRef, useEffect, useCallback, useState,
} from 'react';
import axios from 'axios';
import {
  Button, Tooltip, Space, Spin, message,
} from 'antd';
import {
  BoldOutlined, ItalicOutlined, StrikethroughOutlined,
  UnorderedListOutlined, OrderedListOutlined, CodeOutlined,
} from '@ant-design/icons';
import I18n from 'i18n-js';
import classNames from 'classnames';
import { func, number, string } from 'prop-types';
import { ticket_comment_images_path } from 'js-routes/generated/routes';
import { htmlToMarkdown, markdownToHtml } from './helpers/markdownConverter';

const runCommand = (command, value = null) => document.execCommand(command, false, value);

const currentBlockTag = () => (document.queryCommandValue('formatBlock') || '').toLowerCase();

const IMAGE_MIME = /^image\//u;

const FORMAT_COMMANDS = [
  { key: 'bold', command: 'bold', icon: <BoldOutlined /> },
  { key: 'italic', command: 'italic', icon: <ItalicOutlined /> },
  { key: 'strikethrough', command: 'strikeThrough', icon: <StrikethroughOutlined /> },
  { key: 'bulleted_list', command: 'insertUnorderedList', icon: <UnorderedListOutlined /> },
  { key: 'numbered_list', command: 'insertOrderedList', icon: <OrderedListOutlined /> },
  {
    key: 'quote',
    command: 'formatBlock',
    arg: 'blockquote',
    icon: <span className="ticket-comments-form__toolbar-glyph">&ldquo;</span>,
  },
  {
    key: 'code', command: 'formatBlock', arg: 'pre', icon: <CodeOutlined />,
  },
];

const findImageFile = (list) => [...(list ?? [])]
  .map((item) => (item.getAsFile ? item.getAsFile() : item))
  .find((file) => file && IMAGE_MIME.test(file.type)) ?? null;

const preventBlur = (event) => event.preventDefault();

const CommentEditor = ({
  value = '',
  onChange = (_value) => {},
  ticketId = null,
  placeholder = '',
  height = 160,
}) => {
  const editorRef = useRef(null);
  const emitted = useRef('');
  const [activeFormats, setActiveFormats] = useState({});
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  useEffect(() => {
    if (value === emitted.current) return;

    editorRef.current.innerHTML = markdownToHtml(value);
    emitted.current = value;
  }, [value]);

  const refreshActiveFormats = useCallback(() => {
    const next = {};
    for (const { key, command, arg } of FORMAT_COMMANDS) {
      next[key] = command === 'formatBlock'
        ? currentBlockTag() === arg.toLowerCase()
        : document.queryCommandState(command);
    }
    setActiveFormats(next);
  }, []);

  useEffect(() => {
    const handleSelectionChange = () => {
      const selection = getSelection();
      if (selection.rangeCount && editorRef.current?.contains(selection.anchorNode)) {
        refreshActiveFormats();
      }
    };
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, [refreshActiveFormats]);

  const emitChange = useCallback(() => {
    const markdown = htmlToMarkdown(editorRef.current);
    if (!markdown) editorRef.current.replaceChildren();
    emitted.current = markdown;
    onChange(markdown);
  }, [onChange]);

  const applyCommand = useCallback((command, arg = null) => {
    editorRef.current.focus();
    if (command === 'formatBlock' && currentBlockTag() === arg.toLowerCase()) {
      runCommand('formatBlock', 'div');
    } else {
      runCommand(command, arg);
    }
    emitChange();
    refreshActiveFormats();
  }, [emitChange, refreshActiveFormats]);

  const uploadImage = useCallback(async (file) => {
    const formData = new FormData();
    formData.append('ticket_id', ticketId);
    formData.append('image', file);

    setIsUploadingImage(true);
    try {
      const { data } = await axios.post(ticket_comment_images_path(), formData);
      if (!editorRef.current) return;
      editorRef.current.focus();
      runCommand('insertHTML', `<img src="${data.url}" alt="" />`);
      emitChange();
    } catch {
      message.error(I18n.t('flash.ticket_comments.image_upload_failed'));
    } finally {
      setIsUploadingImage(false);
    }
  }, [ticketId, emitChange]);

  const handlePaste = (event) => {
    const file = findImageFile(event.clipboardData?.items);
    if (!file) return;
    event.preventDefault();
    uploadImage(file);
  };

  const handleDrop = (event) => {
    const file = findImageFile(event.dataTransfer?.files);
    if (!file) return;
    event.preventDefault();
    uploadImage(file);
  };

  const renderButton = ({
    key, command, arg, icon,
  }) => {
    const title = I18n.t(`ticket_comments.toolbar.${key}`);
    const isActive = Boolean(activeFormats[key]);
    return (
      <Tooltip key={key} title={title}>
        <Button
          type="text"
          size="small"
          icon={icon}
          className={classNames('ticket-comments-form__toolbar-btn', {
            'ticket-comments-form__toolbar-btn--active': isActive,
          })}
          aria-label={title}
          aria-pressed={isActive}
          onMouseDown={preventBlur}
          onClick={() => applyCommand(command, arg)}
        />
      </Tooltip>
    );
  };

  return (
    <div className="ticket-comments-form__wysiwyg">
      <div className="ticket-comments-form__toolbar">
        <Space size={0}>
          {FORMAT_COMMANDS.map(renderButton)}
        </Space>
      </div>

      <div
        ref={editorRef}
        className="ticket-comments-form__editable"
        contentEditable
        role="textbox"
        tabIndex={0}
        aria-multiline="true"
        aria-label={placeholder}
        data-placeholder={placeholder}
        style={{ minHeight: height }}
        onInput={emitChange}
        onPaste={handlePaste}
        onDrop={handleDrop}
        onDragOver={(event) => event.preventDefault()}
        onBlur={() => setActiveFormats({})}
        suppressContentEditableWarning
      />

      {isUploadingImage && (
        <div className="ticket-comments-form__upload-overlay">
          <Spin />
        </div>
      )}
    </div>
  );
};

CommentEditor.propTypes = {
  value: string,
  onChange: func,
  ticketId: number,
  placeholder: string,
  height: number,
};

export default CommentEditor;
